import { TagTypes } from "constants/tags";
import { formatValidQueryParamUrlCommaSeparated } from "utils/common";

import { emptySplitApi } from "./empty-split-api";
import {
  Tag,
  TagActionTemplate,
  TagActionTemplateCreate,
  TagActionTemplateFilter,
  TagActionTemplateUpdate,
  TagConvertInput,
  TagCreate,
  TagDuplicateGroup,
  TagFilter,
  TagImportMappingChoice,
  TagImportPreview,
  TagInput,
  TagManagerSummary,
  TagMergeInput,
  TagMergePreview,
  TagsCountResponse,
  TagsResponse
} from "./types/tags.type";

const GET_TAG_QUERY = "{id,name,tooltip,color,type}";

const tagsApi = emptySplitApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getTags: builder.query<TagsResponse, TagFilter>({
      query: (filter) =>
        `global-tags/?${formatValidQueryParamUrlCommaSeparated(filter)}`,
      providesTags: ["Tags"]
    }),
    // ENG-1305: total count for the settings tags admin grid. The list endpoint
    // stays flat (NoCountFlatPagination) so the 16 other callers pay zero count
    // cost; only the admin grid pulls this. Reuses TagFilter so the count
    // matches the active list filters (type, search, is_active, verified).
    getTagsCount: builder.query<TagsCountResponse, TagFilter>({
      query: (filter) =>
        `global-tags/count/?${formatValidQueryParamUrlCommaSeparated(filter)}`,
      providesTags: ["Tags"]
    }),
    getTag: builder.query<Tag, number>({
      query: (tagId) => `/global-tags/${tagId}/?query=${GET_TAG_QUERY}`,
      providesTags: (result) => [
        {
          type: "Tags" as const,
          id: result?.id
        }
      ]
    }),
    createTag: builder.mutation<Tag, TagCreate>({
      query: (newTag) => ({
        url: "global-tags/",
        method: "POST",
        body: newTag
      }),
      invalidatesTags: ["Tags"]
    }),
    updateTag: builder.mutation<Tag, TagInput>({
      query: ({ id, ...updatedTag }) => ({
        url: `global-tags/${id}/`,
        method: "PUT",
        body: updatedTag
      }),
      invalidatesTags: ["Tags"]
    }),
    deleteTag: builder.mutation<Tag, { id: number }>({
      query: ({ id }) => ({
        url: `global-tags/${id}/`,
        method: "DELETE"
      }),
      invalidatesTags: ["Tags"]
    }),
    // --- Tag Manager (Settings > Tags) -------------------------------------
    // Usage count + source for the tags on one grid page. One request per page.
    getTagManagerSummary: builder.query<TagManagerSummary[], number[]>({
      query: (ids) => `global-tags/manager-summary/?ids=${ids.join(",")}`,
      providesTags: ["Tags", "AssignedPatientTags"]
    }),
    getTagDuplicates: builder.query<
      TagDuplicateGroup[],
      { type?: TagTypes } | void
    >({
      query: (filter) =>
        `global-tags/duplicates/?${formatValidQueryParamUrlCommaSeparated(
          filter ?? {}
        )}`,
      providesTags: ["Tags"]
    }),
    previewTagMerge: builder.mutation<TagMergePreview, TagMergeInput>({
      query: (body) => ({
        url: "global-tags/merge-preview/",
        method: "POST",
        body
      })
    }),
    mergeTags: builder.mutation<{ bulk_action_id: number }, TagMergeInput>({
      query: (body) => ({ url: "global-tags/merge/", method: "POST", body }),
      invalidatesTags: ["Tags", "PatientTags", "BulkActions"]
    }),
    convertTag: builder.mutation<
      { bulk_action_id: number },
      { id: number } & TagConvertInput
    >({
      query: ({ id, ...body }) => ({
        url: `global-tags/${id}/convert/`,
        method: "POST",
        body
      }),
      invalidatesTags: ["Tags", "PatientTags", "BulkActions"]
    }),
    hideGlobalTag: builder.mutation<void, number>({
      query: (id) => ({ url: `global-tags/${id}/hide/`, method: "POST" }),
      invalidatesTags: ["Tags", "PatientTags", "HiddenGlobalTags"]
    }),
    unhideGlobalTag: builder.mutation<void, number>({
      query: (id) => ({ url: `global-tags/${id}/unhide/`, method: "POST" }),
      invalidatesTags: ["Tags", "PatientTags", "HiddenGlobalTags"]
    }),
    getHiddenGlobalTags: builder.query<Tag[], void>({
      query: () => "global-tags/hidden/",
      providesTags: ["HiddenGlobalTags"]
    }),
    // Import from CSV: preview parses + matches without writing; commit runs in the background.
    previewTagImport: builder.mutation<TagImportPreview, FormData>({
      query: (body) => ({
        url: "global-tags/import/preview/",
        method: "POST",
        body
      })
    }),
    commitTagImport: builder.mutation<
      { bulk_action_id: number },
      { bulkActionId: number; mapping: TagImportMappingChoice[] }
    >({
      query: ({ bulkActionId, mapping }) => ({
        url: `global-tags/import/${bulkActionId}/commit/`,
        method: "POST",
        body: { mapping }
      }),
      invalidatesTags: ["Tags", "PatientTags", "BulkActions"]
    }),
    getPatientTags: builder.query<TagsResponse, TagFilter | void>({
      query: (filter = {}) =>
        `global-tags/?${formatValidQueryParamUrlCommaSeparated({
          ...filter,
          type: TagTypes.PATIENT
        })}`,
      providesTags: ["PatientTags"]
    }),
    getTagActionTemplates: builder.query<
      TagActionTemplate[],
      TagActionTemplateFilter
    >({
      query: (filter = {}) =>
        `tag-action-templates/?${formatValidQueryParamUrlCommaSeparated(
          filter
        )}`,
      providesTags: ["TagActionTemplates"]
    }),
    createTagActionTemplate: builder.mutation<
      TagActionTemplate,
      TagActionTemplateCreate
    >({
      query: (newTemplate) => ({
        url: "tag-action-templates/",
        method: "POST",
        body: newTemplate
      }),
      invalidatesTags: ["TagActionTemplates", "StaffTasks"]
    }),
    getTagActionTemplate: builder.query<TagActionTemplate, number>({
      query: (id) => `tag-action-templates/${id}/`,
      providesTags: (result) => [
        {
          type: "TagActionTemplates" as const,
          id: result?.id
        }
      ]
    }),
    updateTagActionTemplate: builder.mutation<
      TagActionTemplate,
      TagActionTemplateUpdate
    >({
      query: ({ id, ...updatedTemplate }) => ({
        url: `tag-action-templates/${id}/`,
        method: "PUT",
        body: updatedTemplate
      }),
      invalidatesTags: ["TagActionTemplates", "StaffTasks"]
    }),
    deleteTagActionTemplate: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `tag-action-templates/${id}/`,
        method: "DELETE"
      }),
      invalidatesTags: ["TagActionTemplates", "StaffTasks"]
    })
  })
});

export const {
  useGetTagsQuery,
  useLazyGetTagsQuery,
  useGetTagsCountQuery,
  useGetTagQuery,
  useCreateTagMutation,
  useUpdateTagMutation,
  useDeleteTagMutation,
  useGetPatientTagsQuery,
  useGetTagManagerSummaryQuery,
  useGetTagDuplicatesQuery,
  usePreviewTagMergeMutation,
  useMergeTagsMutation,
  useConvertTagMutation,
  useHideGlobalTagMutation,
  useUnhideGlobalTagMutation,
  useGetHiddenGlobalTagsQuery,
  usePreviewTagImportMutation,
  useCommitTagImportMutation,
  useGetTagActionTemplatesQuery,
  useGetTagActionTemplateMutation,
  useCreateTagActionTemplateMutation,
  useUpdateTagActionTemplateMutation,
  useDeleteTagActionTemplateMutation
} = tagsApi;
